import { Calendar } from '@/components/ui/calendar'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { format } from 'date-fns'
import { CalendarIcon, X } from 'lucide-react'
import { useContext, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormEntityContext } from '../../config/context/forms'
import FieldWrapper from './FieldWrapper'
import { FieldProps } from './types'
import { cn } from '@/lib/utils'

const DateField = ({
  name,
  className,
  inputClassName,
  customLabel,
  customDescription,
  customPlaceholder,
  layout = 'stacked',
  minDate,
  isClearable = false,
  disabled = false,
  onChange,
}: FieldProps<string>) => {
  const form = useFormContext()
  const { i18n, t } = useTranslation()
  const { entityType, i18nPrefix } = useContext(FormEntityContext)
  const [open, setOpen] = useState(false)

  const description =
    customDescription ??
    (i18n.exists(`${i18nPrefix}.${entityType}.${name}.description`)
      ? t(`${i18nPrefix}.${entityType}.${name}.description`)
      : undefined)

  return (
    <div className={cn('w-full', className)}>
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => {
          const selectedDate = field.value ? new Date(field.value) : undefined

          return (
            <FieldWrapper
              layout={layout}
              label={
                customLabel ?? t(`${i18nPrefix}.${entityType}.${name}.label`)
              }
              description={description}
            >
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type='button'
                    variant='outline'
                    disabled={disabled}
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      inputClassName,
                    )}
                  >
                    <CalendarIcon className='mr-2 h-4 w-4' />
                    <span className='grow'>
                      {selectedDate
                        ? format(selectedDate, 'LLL dd, yyyy')
                        : t(
                            customPlaceholder ??
                              `${i18nPrefix}.${entityType}.${name}.placeholder`,
                          )}
                    </span>
                    {isClearable && selectedDate ? (
                      <X
                        className='ml-2 h-4 w-4 opacity-50 hover:opacity-100 transition-opacity'
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          field.onChange(null)
                          onChange?.(null)
                        }}
                      />
                    ) : null}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-auto p-0' align='start'>
                  <Calendar
                    mode='single'
                    weekStartsOn={1}
                    selected={selectedDate}
                    disabled={minDate ? { before: minDate } : undefined}
                    onSelect={(date) => {
                      if (!date) return
                      if (minDate && date < minDate) return
                      const value = format(date, 'yyyy-MM-dd')
                      field.onChange(value)
                      onChange?.(value)
                      setOpen(false)
                    }}
                  />
                </PopoverContent>
              </Popover>
            </FieldWrapper>
          )
        }}
      />
    </div>
  )
}

export default DateField
